// Parses progressive `<update>…</update>` progress blocks out of an AI chat message.
//
// During processing the MCP proxy Lambda streams interim progress steps from the
// router and persists them into the message `content`, wrapped in <update> tags and
// prepended to the final answer (see amplify/.../asyncHandler.js). The UI renders the
// updates as light-grey progress lines, separate from the answer body.

/** A single interim step, in arrival order. `update` = tool/progress label;
 *  `thinking` = admin debug-mode model reasoning (only present when debug enabled). */
export interface ChatStep {
  kind: "update" | "thinking";
  text: string;
}

export interface ParsedUpdates {
  /** Each interim progress step, in order, with the <update> tags stripped. */
  updates: string[];
  /**
   * Admin debug-mode reasoning lines, in order, with the <thinking> tags stripped.
   * Empty for non-debug messages. Emitted by the router only when debug is enabled.
   */
  thinking: string[];
  /**
   * Updates and reasoning interleaved in original arrival order, so the UI can show
   * each reasoning step where it actually happened relative to the tool steps.
   */
  steps: ChatStep[];
  /** The message body with all <update> and <thinking> blocks removed (the final answer). */
  response: string;
}

// Matches either block kind; the backreference \1 ensures the close tag matches the open.
const STEP_REGEX = /<(update|thinking)>([\s\S]*?)<\/\1>/g;

export function parseUpdateBlocks(text: string): ParsedUpdates {
  if (!text) {
    return { updates: [], thinking: [], steps: [], response: "" };
  }

  const steps: ChatStep[] = [];
  let match: RegExpExecArray | null;
  STEP_REGEX.lastIndex = 0;
  while ((match = STEP_REGEX.exec(text)) !== null) {
    const inner = match[2].trim();
    if (inner) {
      steps.push({ kind: match[1] as ChatStep["kind"], text: inner });
    }
  }

  const updates = steps.filter((s) => s.kind === "update").map((s) => s.text);
  const thinking = steps.filter((s) => s.kind === "thinking").map((s) => s.text);

  // Strip both block kinds; trimStart removes the gap they leave at the top of the body.
  const response = text.replace(STEP_REGEX, "").trimStart();
  return { updates, thinking, steps, response };
}

/** A processing step with the reasoning that led up to it nested underneath. */
export interface StepGroup {
  /** The <update> progress label. */
  label: string;
  /** Reasoning lines (debug only) that arrived before this step, in order. */
  thinking: string[];
}

/**
 * Group interleaved steps so each <update> owns the reasoning that preceded it
 * (the router emits a round's reasoning just before the resulting tool step).
 * Reasoning after the last update has no tool step to attach to and is dropped —
 * we never surface a standalone "final reasoning" group.
 */
export function groupStepsByReasoning(steps: ChatStep[]): StepGroup[] {
  const groups: StepGroup[] = [];
  let buffer: string[] = [];
  for (const step of steps) {
    if (step.kind === "thinking") {
      buffer.push(step.text);
    } else {
      groups.push({ label: step.text, thinking: buffer });
      buffer = [];
    }
  }
  return groups;
}
